import React, { useEffect, useState } from "react";
import OtherPlayers from "../../Components/SportsPlayers/OtherPlayers";

import { FiArrowUpRight } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { player_pattern } from "../../Constants/Data";
import PlayerIndividualSkeleton from "../../Components/Skeleton/Players/PlayerIndividualSkeleton";
import { Helmet } from "react-helmet";

function PlayerIndividualPage() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [playerMatches, setPlayerMatches] = useState([]);
  const [matchTypes, setMatchTypes] = useState([]);
  const [selectedMatchType, setSelectedMatchType] = useState(undefined); // undefined = not loaded yet
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const truncateText = (text, maxLength) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Fetch match types once on mount
  useEffect(() => {
    const fetchMatchTypes = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_URL;
        const res = await fetch(`${baseUrl}/api/match-types`);
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setMatchTypes(data.data);
          if (data.data.length > 0) setSelectedMatchType(data.data[0].id);
        } else if (Array.isArray(data)) {
          setMatchTypes(data);
          if (data.length > 0) setSelectedMatchType(data[0].id);
        }
      } catch (err) {
        console.error("Error fetching match types:", err);
      }
    };
    fetchMatchTypes();
  }, []);

  // Fetch player data whenever id or selectedMatchType changes
  // Wait until match types are loaded (selectedMatchType !== undefined)
  useEffect(() => {
    if (selectedMatchType === undefined) return; // not ready yet

    const fetchData = async () => {
      let apiUrl = `${process.env.REACT_APP_API_URL_PLAYERS}/${id}`;
      if (selectedMatchType) {
        apiUrl += `?match_type_id=${selectedMatchType}`;
      }

      try {
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();

        console.log("API Response:", data);

        if (data.success && data.data) {
          const playerData = data.data.player;
          if (playerData && playerData.status === "Published") {
            setPlayer(playerData);
            setStats(data.data.stats || null);
            setPlayerMatches(data.data.player_matches || []);
          } else {
            throw new Error(
              "Player not published or invalid player data format"
            );
          }
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id, selectedMatchType]);

  if (!player) {
    return (
      <div>
        <PlayerIndividualSkeleton />
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const apiUrl = process.env.REACT_APP_API_URL;
  const imagePath = "/storage/player/";

  console.log("All sports players:", player);

  const renderHighScoreOpponent = () => {
    if (!playerMatches || playerMatches.length === 0) return null;

    const highestScoreMatch = playerMatches.reduce(
      (prev, curr) => (curr.runs_scored > prev.runs_scored ? curr : prev),
      playerMatches[0]
    );

    if (!highestScoreMatch.opponent_name) return null;

    const opponentName = highestScoreMatch.opponent_name
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
    return <span>{opponentName}</span>;
  };

  return (
    <>
      <Helmet>
        <title>{`${player.firstname} ${player.lastname} - Player Profile`}</title>
        <meta
          name="description"
          content={`Profile of ${player.firstname} ${player.lastname}, a talented player specializing in ${player.position}.`}
        />
        <meta
          name="keywords"
          content={`${player.firstname}, ${player.lastname}, ${player.position}, sports, player profile`}
        />
        <meta name="author" content="Your Name or Organization" />
        <meta
          property="og:title"
          content={`${player.firstname} ${player.lastname} - Player Profile`}
        />
        <meta
          property="og:description"
          content={`Explore the profile of ${player.firstname} ${player.lastname}, including stats, biography, and more.`}
        />
        <meta
          property="og:image"
          content={
            player.image ? `${apiUrl}${imagePath}${player.image}` : player_pattern
          }
        />
        <meta property="og:url" content={`${window.location.href}`} />
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: `${player.firstname} ${player.lastname}`,
            image: `${apiUrl}${imagePath}${player.image}`,
            jobTitle: player.position,
            birthDate: player.date_of_birth,
            description: truncateText(player.detail, 150),
          })}
        </script>
      </Helmet>

      <div className="bg-header-gradient h-[144px] z-10 "></div>

      {/* PLAYER --///--- (DETAILS) ---- PERSONAL  */}
      <div className="pt-[48px] pb-[108px] font-Rubik md:px-[24px] px-[16px]">
        <div className="container mx-auto">
          <div className="flex flex-col gap-[16px]">
            <div className=" flex flex-col  gap-[12px] b z-10">
              <div className="flex gap-[8px] items-center md:text-[18px] text-[14px]">
                <Link to="/">
                  <div className="flex gap-[4px] items-center text-[#4D4D4D]">
                    <h3>HOME</h3>
                    <FiArrowUpRight />
                  </div>
                </Link>
                <div className="flex gap-[4px] items-center text-[#4D4D4D]">
                  <h3>PLAYERS</h3>
                  <FiArrowUpRight />
                </div>
                <h3 className="text-[#9E120E] uppercase underline leading-[20px]">
                  {player.firstname}
                  <span className="font-bold"> {player.lastname}</span>
                </h3>
              </div>
            </div>

            <div className="flex lg:flex-row flex-col">
              <div className="w-full bg-custom-gradient relative lg:h-[526px] h-full flex lg:flex-row flex-col z-10 rounded-[12px] font-Rubik cursor-pointer">
                <div className=" flex justify-center lg:hidden ">
                  <img
                    src={player_pattern}
                    alt=""
                    className=" absolute -z-10"
                  />
                </div>
                <div className="flex lg:flex-row flex-col-reverse w-full lg:pl-[32px]">
                  <div className="individual-card-content flex w-full items-center">
                    <div className="flex flex-col gap-[16px] w-full lg:items-start items-center bg-[#980C0C] pt-[12px] lg:pt-0 lg:bg-transparent">
                      <h3 className="uppercase flex flex-col lg:text-[36px] text-[24px] lg:text-[#9E120E] text-white">
                        {player.firstname}
                        <span className="font-bold"> {player.lastname}</span>
                      </h3>

                      <h4 className="uppercase bg-[#E0E0E0] text-[14px] p-[8px] text-[#3F3F3F] font-medium rounded-full">
                        {player.position}
                      </h4>
                      <div className="flex flex-col items-center lg:items-start gap-[16px] border-t-[1px] w-full lg:w-[480px] justify-center lg:justify-start border-[#CECECE] lg:text-black text-white">
                        <div className="flex border-b-[1px] lg:border-hidden w-full justify-center lg:justify-start">
                          <div className="flex flex-col py-[12px] px-[16px] gap-[12px] text-center lg:text-start border-r-[1px] text-[14px] border-[#CECECE]">
                            <h4>BORN</h4>
                            <h4 className="font-medium">
                              {player?.date_of_birth
                                ? formatDate(player.date_of_birth)
                                : "-"}
                            </h4>
                          </div>

                          <div className="flex flex-col py-[12px] px-[16px] gap-[12px] text-center capitalize lg:text-start lg:border-r-[1px] text-[14px] border-[#CECECE]">
                            <h4>BATTING STYLE</h4>
                            <h4 className="font-medium">
                              {player.batting_style}
                            </h4>
                          </div>

                          <div className="lg:flex flex-col py-[12px] capitalize px-[16px] gap-[12px] hidden text-[14px]">
                            <h4>BOWLING STYLE</h4>
                            <h4 className="font-medium">
                              {player.bowling_style}
                            </h4>
                          </div>
                        </div>

                        <div className="lg:flex flex-col gap-[8px] hidden">
                          <h3 className="text-[#212121] text-[20px] font-medium italic uppercase">
                            about <span>{player.name}</span>
                          </h3>
                          <p className="lg:text-[16px] text-[12px] whitespace-pre-wrap">
                            {player.detail}
                          </p>
                        </div>
                        <div className="flex flex-col capitalize py-[12px] px-[16px] gap-[12px] lg:hidden text-center text-[14px]">
                          <h4>BOWLING STYLE</h4>
                          <h4 className="font-medium">{player.bowling_style}</h4>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center w-full">
                    <img
                      src={
                        player.image
                          ? `${apiUrl}${imagePath}${player.image}`
                          : ""
                      }
                      alt={player.name}
                      className="object-cover"
                    />
                    <div className=" lg:flex hidden flex-row-reverse">
                      <img
                        src={player_pattern}
                        alt=""
                        className=" absolute -z-10"
                      />
                    </div>
                  </div>
                </div>

                {/* ---- STATS SIDEBAR ---- */}
                <div className="lg:bg-[#980C0C] bg-[#DAD9DC] lg:w-[250px] flex flex-col lg:gap-0 gap-[12px] justify-between items-center lg:text-white text-[#212121] lg:rounded-r-[12px] py-[32px] px-[24px]">

                  {/* Match Type Filter Dropdown */}
                  {matchTypes.length > 0 && (
                    <div className="w-full pb-[12px] border-b-[1px] border-[#ffffff30] relative">
                      {/* Trigger button */}
                      <button
                        onClick={() => setDropdownOpen((o) => !o)}
                        className="w-full flex items-center justify-between gap-[8px] rounded-[8px] px-[12px] py-[9px] bg-white border-[2px] border-[#fff] text-[#980C0C] text-[12px] font-bold uppercase tracking-wide shadow-lg transition-all hover:bg-[#fff0f0] focus:outline-none"
                      >
                        <span>
                          {selectedMatchType
                            ? (matchTypes.find((mt) => mt.id === selectedMatchType)?.name ||
                              matchTypes.find((mt) => mt.id === selectedMatchType)?.match_type ||
                              matchTypes.find((mt) => mt.id === selectedMatchType)?.type ||
                              "Select Type")
                            : "Select Type"}
                        </span>
                        {/* Chevron */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`w-[14px] h-[14px] shrink-0 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : "rotate-0"}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {/* Options list */}
                      {dropdownOpen && (
                        <div className="absolute left-0 right-0 top-[calc(100%-8px)] z-50 mt-[4px] rounded-[8px] overflow-hidden shadow-xl border border-white/10 bg-[#1a0404]">
                          {matchTypes.map((mt) => {
                            const label = mt.name || mt.match_type || mt.type;
                            const isActive = selectedMatchType === mt.id;
                            return (
                              <button
                                key={mt.id}
                                onClick={() => {
                                  setSelectedMatchType(mt.id);
                                  setDropdownOpen(false);
                                }}
                                className={`w-full text-left px-[14px] py-[10px] text-[12px] font-semibold uppercase tracking-wide transition-colors
                                  ${isActive
                                    ? "bg-[#980C0C] text-white"
                                    : "text-white/80 hover:bg-white/10"
                                  }`}
                              >
                                {isActive && <span className="mr-[6px]">✓</span>}
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {/* 
                  <div className=" lg:w-[148px] w-full lg:text-[22px] text-[18px] font-bold p-4 border-b-[1px] border-[#C6C6C6]">
                    <h3>
                      {selectedMatchType
                        ? (matchTypes.find((mt) => mt.id === selectedMatchType)?.name ||
                          matchTypes.find((mt) => mt.id === selectedMatchType)?.match_type ||
                          matchTypes.find((mt) => mt.id === selectedMatchType)?.type ||
                          "STATS")
                        : "ALL STATS"}
                    </h3>
                  </div> */}

                  <div className="flex lg:flex-col flex-row-reverse lg:justify-center justify-between w-full items-center lg:text-[24px] text-[14px] font-bold px-4 py-2 border-b-[1px] border-[#C6C6C6]">
                    <h3>{stats?.total_matches ?? 0}</h3>
                    <h3 className="text-[12px] lg:text-[#EDEDED] font-normal">
                      MATCHES
                    </h3>
                  </div>

                  <div className="flex lg:flex-col flex-row-reverse lg:justify-center justify-between w-full items-center lg:text-[24px] text-[14px] font-bold px-4 py-2 border-b-[1px] border-[#C6C6C6]">
                    <h3>{stats?.total_runs ?? 0}</h3>
                    <h3 className="text-[12px] lg:text-[#EDEDED] font-normal">
                      TOTAL RUNS
                    </h3>
                  </div>

                  <div className="flex lg:flex-col flex-row-reverse lg:justify-center justify-between w-full items-center lg:text-[24px] text-[14px] font-bold px-4 py-2 border-b-[1px] border-[#C6C6C6]">
                    <div className="flex gap-[8px]">
                      <h3>{stats?.highest_innings_run ?? 0}</h3>
                      {/* {renderHighScoreOpponent() && <span>v</span>}
                      <div className="uppercase">{renderHighScoreOpponent()}</div> */}
                    </div>
                    <h3 className="text-[12px] lg:text-[#EDEDED] font-normal">
                      HIGHEST SCORE
                    </h3>
                  </div>

                  <div className="flex lg:flex-col flex-row-reverse lg:justify-center justify-between w-full items-center lg:text-[24px] text-[14px] font-bold px-4 py-2 border-b-[1px] border-[#C6C6C6]">
                    <h3>{stats?.total_wickets ?? 0}</h3>
                    <h3 className="text-[12px] lg:text-[#EDEDED] font-normal">
                      TOTAL WICKETS
                    </h3>
                  </div>

                  <div className="flex lg:flex-col flex-row-reverse lg:justify-center justify-between w-full items-center lg:text-[24px] text-[14px] font-bold px-4 py-2 border-b-[1px] border-[#C6C6C6]">
                    <h3>
                      {stats?.highest_innings_wicket
                        ? `${stats.highest_innings_wicket.wickets}/${stats.highest_innings_wicket.runs_given}`
                        : "0/0"}
                    </h3>
                    <h3 className="text-[12px] lg:text-[#EDEDED] font-normal">
                      BEST WICKETS
                    </h3>
                  </div>

                  <div className="flex flex-col gap-[8px] lg:hidden">
                    <h3 className="text-[#212121] text-[16px] font-medium italic uppercase">
                      about <span>{player.name}</span>
                    </h3>
                    <p className="text-[14px] whitespace-pre-wrap">{player.detail}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* !!!! --- /// --- personal --- details -- */}

      {/* --- other --- player ==== card ++++ */}
      <div>
        <OtherPlayers idIndivitual={player.id} />
      </div>
      {/* ****CARD --//-- PLAYER -- ''' OTHER */}
    </>
  );
}

export default PlayerIndividualPage;
